import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconSealySizeCaKing from "../../../../img/icons/sealy-size-ca-king.svg";
import iconSealySizeFull from "../../../../img/icons/sealy-size-full.svg";
import iconSealySizeKing from "../../../../img/icons/sealy-size-king.svg";
import iconSealySizeQueen from "../../../../img/icons/sealy-size-queen.svg";
import iconSealySizeSplitKing from "../../../../img/icons/sealy-size-split-king.svg";
import iconSealySizeSplitQueen from "../../../../img/icons/sealy-size-split-queen.svg";
import iconSealySizeTwinLong from "../../../../img/icons/sealy-size-twin-long.svg";
import iconSealySizeTwin from "../../../../img/icons/sealy-size-twin.svg";
import {
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../../models/catalogue";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { PLCInfoPanel } from "../PLCInfoPanel";
import { defaults } from "../defaults";
import {
    IPLCMattressSizeOptionSelector,
    IPLCOptionPanel,
    IPLCProductCategorySelector,
} from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setOptionValue } from "../reducers";
import { ISelectedCategories } from "../reducers.interfaces";
import { selectedCategoriesSelector } from "../selectors";
import { getOptionValueByIdx } from "../utils";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-mattress-size-option-selector";

const SIZES = [
    {
        label: t`Twin`,
        value: "Twin",
        iconPath: iconSealySizeTwin,
    },
    {
        label: t`Twin Long`,
        value: "Twin Long",
        iconPath: iconSealySizeTwinLong,
    },
    {
        label: t`Full`,
        value: "Full",
        iconPath: iconSealySizeFull,
    },
    {
        label: t`Queen`,
        value: "Queen",
        iconPath: iconSealySizeQueen,
    },
    {
        label: t`Split Queen`,
        value: "Split Queen",
        iconPath: iconSealySizeSplitQueen,
    },
    {
        label: t`King`,
        value: "King",
        iconPath: iconSealySizeKing,
    },
    {
        label: t`Split King`,
        value: "Split King",
        iconPath: iconSealySizeSplitKing,
    },
    {
        label: t`CA King`,
        value: "CA King",
        iconPath: iconSealySizeCaKing,
    },
    {
        label: t`Split CA King`,
        value: "Split CA King",
        iconPath: iconSealySizeSplitKing,
    },
];

const SIZE_ATTR = "option_size";

interface SizeOption {
    label: string;
    value: string;
    iconPath: string;
}

/**
 * Check if selecting this option will result in a perfect product match or not. If
 * not, the option should be visually disabled (but still actually selectable).
 */
const isOptionDisabled = (
    option: IOptionSingleValue,
    optionValues: IOptionValues,
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    rootProducts: readonly IProduct[],
    selectedCategories: ISelectedCategories,
): boolean => {
    const futureOptionValues = {
        ...optionValues,
        [SIZE_ATTR]: option,
    };
    const result = getMostSimilarProduct({
        categorySelectors,
        optionPanels,
        rootProducts,
        selectedCategories,
        optionValues: futureOptionValues,
        previouslySelectedCategories: selectedCategories,
        previouslySelectedOptionValues: optionValues,
    });
    return !result || result.distance > 0;
};

const renderSizeOption = (
    opt: SizeOption,
    groupID: string,
    isChecked: boolean,
    isDisabled: boolean,
    onChange: (value: string) => void,
): React.ReactNode => {
    const optionID = `${groupID}-${opt.value}`
        .replace(/[^a-zA-Z0-9-]/gi, "")
        .toLowerCase();
    const listElementClass = classNames({
        [`${BEM_NAME}__option`]: true,
        [`${BEM_NAME}__option--selected`]: isChecked,
        [`${BEM_NAME}__option--faux-disabled`]: isDisabled,
    });
    const inputClass = classNames({
        [`${BEM_NAME}__input`]: true,
        [`${BEM_NAME}__input--faux-disabled`]: isDisabled,
    });

    return (
        <li
            key={opt.value}
            className={listElementClass}
            onClick={() => {
                if (!isChecked) {
                    onChange(opt.label);
                }
            }}
        >
            <div>
                <SVG src={opt.iconPath} />
            </div>
            <input
                type="radio"
                className={inputClass}
                id={optionID}
                name={groupID}
                onChange={(e) => {
                    if (e.currentTarget.checked) {
                        onChange(opt.value);
                    }
                }}
                checked={isChecked}
                value={opt.value}
            />
            <label
                htmlFor={optionID}
                className={`${BEM_NAME}__label`}
                aria-selected={false}
            >
                {opt.label}
            </label>
        </li>
    );
};

export const PLCMattressSizeOptionSelector = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        selector: IPLCMattressSizeOptionSelector;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const rootProducts = useAppSelector(
        (rootState) =>
            (rootState.configurator || defaults).entities.rootProducts,
    );
    const selectedCategories = useAppSelector((rootState) =>
        selectedCategoriesSelector(rootState.configurator || defaults),
    );
    const optionValues = useAppSelector(
        (rootState) => (rootState.configurator || defaults).ui.optionValues,
    );
    const value = useAppSelector((rootState) =>
        getOptionValueByIdx(
            (rootState.configurator || defaults).ui.optionValues[SIZE_ATTR],
            0,
        ),
    );

    const handleChange = (optionValue: string) => {
        dispatch(
            setOptionValue({
                namespace: "",
                code: SIZE_ATTR,
                index: 0,
                totalNumValues: 1,
                value: optionValue,
            }),
        );
    };

    const groupID = props.selector.id;

    return (
        <fieldset className={BEM_NAME}>
            <legend>
                <span>{t`Mattress Size`}</span>
            </legend>
            <ul>
                {SIZES.map((opt) => {
                    const isChecked = value === opt.value;
                    const isDisabled = isOptionDisabled(
                        opt.value,
                        optionValues,
                        props.categorySelectors,
                        props.optionPanels,
                        rootProducts,
                        selectedCategories,
                    );
                    return renderSizeOption(
                        opt,
                        groupID,
                        isChecked,
                        isDisabled,
                        handleChange,
                    );
                })}
            </ul>
            <PLCInfoPanel
                buttonText={t`Which size is right for me?`}
                content={props.selector.value.help_content}
            />
        </fieldset>
    );
};
